/**
 * "Bexio Zahlungsabgleich" — the reconciliation summary, compacted.
 *
 * Every figure comes from `GET /api/admin/bexio/reconciliation`. The wording
 * below is the section's existing statement, kept verbatim: the Bexio mirror is
 * read-only and Bexio is what decides open vs. paid.
 *
 * What this card must NEVER become: a monthly comparison. The reconciliation
 * totals are cumulative across all mapped Bexio invoices and carry no reliable
 * month attribution, which is why `direct_delta_available` is false on the
 * side-by-side endpoint. Putting an expected-versus-actual figure here would be
 * arithmetic across two different time spans.
 */
import React from "react";

import { formatDateTime } from "../../../../utils/datetime";
import { fmtMoney } from "../bexioFormat";

/**
 * A labelled money figure.
 *
 * The amount is never truncated. An elided number is not a smaller number, it is
 * a WRONG one — "CHF 16'940.00" rendered as "CHF 16'940…" reads as a different
 * value, and on a reconciliation card that is the one thing that must not happen.
 * So: no `truncate` here, `whitespace-nowrap` so the amount never breaks mid-
 * number, and the size steps up only once there is room for it. The LABEL may
 * still truncate — a clipped word is recoverable, a clipped amount is not.
 */
function Figure({ label, value, strong }) {
  return (
    <div className="min-w-0">
      <div className="truncate text-xs text-muted-foreground">{label}</div>
      <div
        className={`mt-1 whitespace-nowrap tabular-nums ${
          strong
            ? "text-base font-semibold text-foreground"
            : "text-sm text-foreground"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

export default function BexioPaymentSummaryCard({ reconciliation }) {
  const s = reconciliation?.summary;
  const currency = s?.currency || "CHF";
  const stale = reconciliation?.warnings?.sync_stale?.is_stale === true;

  return (
    <section
      aria-labelledby="bexio-payment-summary-title"
      className="rounded-xl border border-border bg-card p-5"
    >
      <h2
        id="bexio-payment-summary-title"
        className="text-sm font-semibold text-foreground"
      >
        Bexio Zahlungsabgleich
      </h2>
      <p className="mt-1 text-xs text-muted-foreground">
        Schreibgeschützter Abgleich aus dem Bexio-Spiegel. Bexio ist führend für
        offen/bezahlt.
      </p>

      {!s ? (
        <p className="mt-6 text-sm text-muted-foreground">
          Keine Abgleichsdaten verfügbar.
        </p>
      ) : (
        <>
          {/* Two columns until the card is genuinely wide enough for three.
              Measured in the browser against the built CSS: this card is 7 of 20
              row columns from `lg` up, so the old `sm:grid-cols-3` left 85px per
              figure at 1024px while "CHF 16'940.00" needs 122px — that is where
              the clipped amounts came from. Three columns only from `2xl`, where
              the cell is ~145px and even a seven-figure total fits. */}
          <div className="mt-4 grid grid-cols-1 gap-x-4 gap-y-3 border-t border-border pt-4 sm:grid-cols-2 2xl:grid-cols-3">
            <Figure label="Offen fällig" value={fmtMoney(s.open_due, currency)} strong />
            <Figure
              label="Offen noch nicht fällig"
              value={fmtMoney(s.open_not_yet_due_or_no_due_date, currency)}
              strong
            />
            <Figure label="Offen Total" value={fmtMoney(s.open_total, currency)} strong />
          </div>

          <div className="mt-4 grid grid-cols-1 gap-x-4 gap-y-3 border-t border-border pt-4 sm:grid-cols-2">
            <Figure label="Bezahlt" value={fmtMoney(s.paid_total_invoice_level, currency)} />
            <Figure
              label="Fakturiert Total"
              value={fmtMoney(s.invoice_total_counted, currency)}
            />
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
            <div className="text-xs text-muted-foreground">
              Stand: {formatDateTime(reconciliation.generated_at)}
            </div>
            {/* Freshness is a real backend verdict (`sync_stale`), not a guess
                from the timestamp above it. */}
            <span
              className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${
                stale
                  ? "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
                  : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-400"
              }`}
            >
              {stale ? "Rechnungs-Sync veraltet" : "Rechnungs-Sync aktuell"}
            </span>
          </div>
        </>
      )}
    </section>
  );
}
